import { v4 as uuid } from 'uuid';
import { cookie } from '@turnercode/cdp-cookie';
import { hasLocalStorage } from '../../utils/browser';

/**
 * Identity resolution for Mosaic (Permutive-shaped) events.
 *
 * - user_id waterfall: Bolt user id > device id > WMUKID (per Mosaic).
 * - identity_type: which tier of that waterfall user_id came from.
 * - view_id: a per-pageview UUID, stable across all events fired within the
 *   same pageview and rotated on each new pageview.
 *
 * The Bolt device id is written by the Bolt/GI web SDK to localStorage under
 * `BOLT_DEVICE_ID` (confirmed on CNNi, same-origin and JS-readable). The Bolt
 * user id lives only inside the HttpOnly, cross-domain Bolt auth token and is
 * not readable on editorial pages, so it is absent today and the waterfall
 * degrades to the device id, then the WMUKID (see DAP-116164).
 */

// localStorage key the Bolt/GI web SDK uses for the device id.
export const BOLT_DEVICE_ID_STORAGE_KEY = 'BOLT_DEVICE_ID';
// Legacy/optional cookie fallbacks (may differ by brand; absent values simply
// fall through to the next tier of the waterfall).
export const BOLT_USER_ID_COOKIE = 'bolt_user_id';
export const BOLT_DEVICE_ID_COOKIE = 'bolt_device_id';

export type UserIdSources = {
  boltUserId?: string | null;
  deviceId?: string | null;
  wmukid?: string | null;
};

/** Which tier of the waterfall a `user_id` came from. Emitted as the top-level
 * `identity_type` so Mosaic can tell the identifier space apart downstream
 * without re-deriving it (DAP-115270). */
export type IdentityType = 'BOLT_USER_ID' | 'DEVICE_ID' | 'WMUKID';

export type ResolvedIdentity = {
  user_id: string;
  /** `null` when no tier resolved, matching the feed's convention that an
   * unknown value is null rather than an empty string. */
  identity_type: IdentityType | null;
};

// Ordered highest- to lowest-precedence. The identity_type label and the source
// it reads from are declared together so the two cannot drift apart.
const WATERFALL: { type: IdentityType; source: keyof UserIdSources }[] = [
  { type: 'BOLT_USER_ID', source: 'boltUserId' },
  { type: 'DEVICE_ID', source: 'deviceId' },
  { type: 'WMUKID', source: 'wmukid' },
];

function isUsable(value: string | null | undefined): value is string {
  return typeof value === 'string' && value.trim() !== '' && value !== 'Unknown';
}

/**
 * Resolve the Permutive `user_id` via the waterfall (Bolt user id, then device
 * id, then WMUKID) along with the tier it came from.
 */
export function resolveIdentity(sources: UserIdSources): ResolvedIdentity {
  for (const tier of WATERFALL) {
    const value = sources[tier.source];
    if (isUsable(value)) {
      return { user_id: value, identity_type: tier.type };
    }
  }
  return { user_id: '', identity_type: null };
}

/**
 * Resolve the Permutive `user_id` via the waterfall:
 * Bolt user id, then device id, then WMUKID.
 */
export function resolveUserId(sources: UserIdSources): string {
  return resolveIdentity(sources).user_id;
}

/** Read the Bolt user id, if exposed client-side (typically absent on
 * editorial web; the token that carries it is HttpOnly + cross-domain). */
export function getBoltUserId(): string | null {
  return (cookie.get(BOLT_USER_ID_COOKIE) as string) || null;
}

/**
 * Read the Bolt device id. Prefers the Bolt/GI web SDK's localStorage entry
 * (`BOLT_DEVICE_ID`), falling back to a cookie of the same intent.
 */
export function getDeviceId(): string | null {
  try {
    if (hasLocalStorage()) {
      const fromStorage = window.localStorage.getItem(BOLT_DEVICE_ID_STORAGE_KEY);
      if (fromStorage) {
        return fromStorage;
      }
    }
  } catch {
    // ignore storage access errors (e.g. private mode) and fall through
  }
  return (cookie.get(BOLT_DEVICE_ID_COOKIE) as string) || null;
}

// Per-pageview view_id. Module-level so it is shared by every event fired
// within a pageview, mirroring the SDK's existing module-level identity state.
let currentViewId = '';

/** Rotate the view_id for a new pageview and return it. */
export function newViewId(): string {
  currentViewId = uuid();
  return currentViewId;
}

/** Return the current pageview's view_id, generating one if none exists yet. */
export function getViewId(): string {
  if (!currentViewId) {
    currentViewId = uuid();
  }
  return currentViewId;
}

/** Clear the current view_id (primarily for tests). */
export function resetViewId(): void {
  currentViewId = '';
}
